// src/utils/professionColors.js
// Utility for profession-specific color theming

export const getProfessionColors = (profession) => {
  switch (profession?.toUpperCase()) {
    case 'INFORMATIKER':
    case 'INFORMATIK':
    case 'IT':
      return {
        primary: 'informatik-blue-500',
        light: 'informatik-blue-100',
        dark: 'informatik-blue-700',
        text: 'informatik-blue-800',
        bg: 'informatik-blue-50',
        border: 'informatik-blue-200',
        hover: 'informatik-blue-600',
        gradient: 'from-informatik-blue-500 to-informatik-blue-600',
        css: {
          primary: '#3B82F6',
          light: '#DBEAFE',
          dark: '#1D4ED8',
          text: '#1E40AF',
          bg: '#EFF6FF',
          border: '#BFDBFE'
        }
      };
      
    case 'MEDIAMATIKER':
    case 'MEDIAMATIK':
    case 'MEDIA':
      return {
        primary: 'mediamatik-purple-500',
        light: 'mediamatik-purple-100',
        dark: 'mediamatik-purple-700',
        text: 'mediamatik-purple-800',
        bg: 'mediamatik-purple-50',
        border: 'mediamatik-purple-200',
        hover: 'mediamatik-purple-600',
        gradient: 'from-mediamatik-purple-500 to-mediamatik-purple-600',
        css: {
          primary: '#A855F7',
          light: '#F3E8FF',
          dark: '#7C3AED',
          text: '#6B21A8',
          bg: '#FAF5FF',
          border: '#E9D5FF'
        }
      };

    case 'KV':
    case 'KAUFMANN':
    case 'KAUFFRAU':
      return {
        primary: 'gray-500',
        light: 'gray-100',
        dark: 'gray-700',
        text: 'gray-800',
        bg: 'gray-50',
        border: 'gray-200',
        hover: 'gray-600',
        gradient: 'from-gray-500 to-gray-600',
        css: {
          primary: '#6B7280',
          light: '#F3F4F6',
          dark: '#374151',
          text: '#1F2937',
          bg: '#F9FAFB',
          border: '#E5E7EB'
        }
      };

    case 'KUNDENDIALOG':
      return {
        primary: 'emerald-500',
        light: 'emerald-100',
        dark: 'emerald-700',
        text: 'emerald-800',
        bg: 'emerald-50',
        border: 'emerald-200',
        hover: 'emerald-600',
        gradient: 'from-emerald-500 to-emerald-600',
        css: {
          primary: '#10B981',
          light: '#D1FAE5',
          dark: '#047857',
          text: '#065F46',
          bg: '#ECFDF5',
          border: '#A7F3D0'
        }
      };

    case 'DETAILHANDEL':
      return {
        primary: 'orange-500',
        light: 'orange-100',
        dark: 'orange-700',
        text: 'orange-800',
        bg: 'orange-50',
        border: 'orange-200',
        hover: 'orange-600',
        gradient: 'from-orange-500 to-orange-600',
        css: {
          primary: '#F97316',
          light: '#FFEDD5',
          dark: '#C2410C',
          text: '#9A3412',
          bg: '#FFF7ED',
          border: '#FED7AA'
        }
      };
      
    case 'MIXED':
      return {
        primary: 'sunrise-red',
        light: 'pale-dawn',
        dark: 'sunrise-700',
        text: 'grey-dusk',
        bg: 'ice-white',
        border: 'pale-dawn',
        hover: 'sunrise-600',
        gradient: 'from-informatik-blue-400 via-sunrise-red to-mediamatik-purple-400',
        css: {
          primary: '#DA291C',
          light: '#E6E3DF',
          dark: '#991B1B',
          text: '#3D3935',
          bg: '#FFFFFF',
          border: '#E6E3DF'
        }
      };
      
    default:
      return {
        primary: 'sunrise-red',
        light: 'sunrise-100',
        dark: 'sunrise-700',
        text: 'sunrise-800',
        bg: 'sunrise-50',
        border: 'sunrise-200',
        hover: 'sunrise-600',
        gradient: 'from-sunrise-red to-sunrise-600',
        css: {
          primary: '#DA291C',
          light: '#FEE2E2',
          dark: '#991B1B',
          text: '#7F1D1D',
          bg: '#FEF2F2',
          border: '#FECACA'
        }
      };
  }
};

export const getProfessionPillClasses = (profession) => {
  const colors = getProfessionColors(profession);
  return `bg-${colors.light} text-${colors.text}`;
};

export const getProfessionButtonClasses = (profession, variant = 'primary') => {
  const colors = getProfessionColors(profession);
  
  switch (variant) {
    case 'primary':
      return `bg-${colors.primary} hover:bg-${colors.hover} text-white`;
    case 'secondary':
      return `bg-${colors.light} hover:bg-${colors.border} text-${colors.text}`;
    case 'outline':
      return `border border-${colors.primary} text-${colors.primary} hover:bg-${colors.primary} hover:text-white`;
    default:
      return `bg-${colors.primary} hover:bg-${colors.hover} text-white`;
  }
};

export const getProfessionCardClasses = (profession) => {
  const colors = getProfessionColors(profession);
  return `bg-${colors.bg} border-${colors.border}`;
};

export const getProfessionIconClasses = (profession) => {
  const colors = getProfessionColors(profession);
  return `text-${colors.primary}`;
};

// Hook for dynamic profession styling
export const useProfessionTheme = (profession) => {
  return getProfessionColors(profession);
};

// Default unified brand colors (always available)
export const brandColors = {
  primary: 'sunrise-red',
  secondary: 'grey-dusk',
  background: 'pale-dawn',
  surface: 'ice-white',
  accent: 'green-cloud',
  text: 'grey-dusk',
  textLight: 'slate-cloud'
};
// Tailwind Safelist (to ensure dynamic classes are generated)
// bg-informatik-blue-500 text-informatik-blue-500 border-informatik-blue-500 bg-informatik-blue-100 text-informatik-blue-800 border-informatik-blue-200
// bg-mediamatik-purple-500 text-mediamatik-purple-500 border-mediamatik-purple-500 bg-mediamatik-purple-100 text-mediamatik-purple-800 border-mediamatik-purple-200
// bg-gray-500 text-gray-500 border-gray-500 bg-gray-100 text-gray-800 border-gray-200
// bg-emerald-500 text-emerald-500 border-emerald-500 bg-emerald-100 text-emerald-800 border-emerald-200
// bg-orange-500 text-orange-500 border-orange-500 bg-orange-100 text-orange-800 border-orange-200
// bg-sunrise-red text-sunrise-red border-sunrise-red bg-sunrise-100 text-sunrise-800 border-sunrise-200
